/**
 * Servicio para gestionar el nivel de acceso del usuario
 * 
 * Este servicio se comunica con el backend para obtener información sobre:
 * - Si el usuario tiene NFT
 * - Nivel de acceso (full | limited)
 * - Si puede comprar LP
 * - Si requiere sponsor al comprar NFT
 * 
 * Endpoints del backend:
 * - GET /users/me/access-level
 * - GET /users/can-purchase-lp
 * - GET /users/nft-purchase-validation
 */

import { jwtApiService } from './jwtApiService';

/**
 * Información completa del nivel de acceso del usuario
 */
export interface AccessLevelInfo {
  hasNFT: boolean;
  accessLevel: 'full' | 'limited';
  canPurchaseLP: boolean;
  requiresSponsorAtPurchase: boolean;
}

/**
 * Obtiene la información completa del nivel de acceso del usuario
 * @returns {Promise<AccessLevelInfo>} Información de acceso
 */
export async function getAccessLevel() {
  try {
    const endpoint = '/users/me/access-level';
    const response = await jwtApiService.get(endpoint);
    
    return {
      success: true,
      data: {
        hasNFT: response.hasNFT || false,
        accessLevel: response.accessLevel || 'limited',
        canPurchaseLP: response.canPurchaseLP || false,
        requiresSponsorAtPurchase: response.requiresSponsorAtPurchase || false,
      },
    };
  } catch (error) {
    console.error('[UserAccessService] Error obteniendo nivel de acceso:', error);
    
    // Si es 401, el usuario no está autenticado
    if (error.response?.status === 401) {
      return {
        success: false,
        error: 'UNAUTHORIZED',
        data: {
          hasNFT: false,
          accessLevel: 'limited',
          canPurchaseLP: false,
          requiresSponsorAtPurchase: false,
        },
      };
    }
    
    // En caso de error, retornar acceso limitado por defecto
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Error desconocido',
      data: {
        hasNFT: false,
        accessLevel: 'limited',
        canPurchaseLP: false,
        requiresSponsorAtPurchase: false,
      },
    };
  }
}

/**
 * Verifica si el usuario puede comprar LP
 * @returns {Promise<{canPurchaseLP: boolean, hasNFT: boolean, message?: string}>}
 */
export async function canPurchaseLP() {
  try {
    const endpoint = '/users/can-purchase-lp';
    const response = await jwtApiService.get(endpoint);
    
    return {
      success: true,
      canPurchaseLP: response.canPurchaseLP || false,
      hasNFT: response.hasNFT || false,
      message: response.message,
    };
  } catch (error) {
    console.error('[UserAccessService] Error verificando compra LP:', error);
    
    if (error.response?.status === 401) {
      return {
        success: false,
        error: 'UNAUTHORIZED',
        canPurchaseLP: false,
        hasNFT: false,
        message: 'You must be authenticated to purchase LP.',
      };
    }
    
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      canPurchaseLP: false,
      hasNFT: false,
      message: 'You must purchase a Premium NFT ($30) before you can purchase LP.',
    };
  }
}

/**
 * Valida los requisitos para comprar NFT
 * @returns {Promise<{requiresSponsorAtPurchase: boolean, hasNFT: boolean, message?: string}>}
 */
export async function validateNftPurchase() {
  try {
    const endpoint = '/users/nft-purchase-validation';
    const response = await jwtApiService.get(endpoint);
    
    return {
      success: true,
      requiresSponsorAtPurchase: response.requiresSponsorAtPurchase || false,
      hasNFT: response.hasNFT || false,
      message: response.message,
    };
  } catch (error) {
    console.error('[UserAccessService] Error validando compra NFT:', error);
    
    if (error.response?.status === 401) {
      return {
        success: false,
        error: 'UNAUTHORIZED',
        requiresSponsorAtPurchase: false,
        hasNFT: false,
        message: 'You must be authenticated to purchase NFT.',
      };
    }
    
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      requiresSponsorAtPurchase: false,
      hasNFT: false,
      message: undefined,
    };
  }
}

const userAccessService = {
  getAccessLevel,
  canPurchaseLP,
  validateNftPurchase,
};
export default userAccessService;
